import { useEffect, useState } from 'react'
import { Mail, Package, Phone, User } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { useCheckoutFormStore } from '@/store/stores/checkoutFormStore'
import { submitProductRequest } from '@/services/productRequests'
import { isFirebaseConfigured } from '@/lib/firebase'
import toast from 'react-hot-toast'

interface ProductRequestModalProps {
  open: boolean
  onClose: () => void
  defaultProductName?: string
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function ProductRequestModal({
  open,
  onClose,
  defaultProductName = '',
}: ProductRequestModalProps) {
  const fullName = useCheckoutFormStore((s) => s.fullName)
  const email = useCheckoutFormStore((s) => s.email)
  const phone = useCheckoutFormStore((s) => s.phone)
  const setFullName = useCheckoutFormStore((s) => s.setFullName)
  const setEmail = useCheckoutFormStore((s) => s.setEmail)
  const setPhone = useCheckoutFormStore((s) => s.setPhone)

  const [productName, setProductName] = useState(defaultProductName)
  const [quantity, setQuantity] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setProductName(defaultProductName)
      setQuantity('')
    }
  }, [open, defaultProductName])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!fullName.trim() || fullName.trim().length < 2) {
      toast.error('Please enter your full name.')
      return
    }
    if (!isValidEmail(email.trim())) {
      toast.error('Please enter a valid email address.')
      return
    }
    if (phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid phone number.')
      return
    }
    if (!productName.trim()) {
      toast.error('Please enter the product name.')
      return
    }
    const qty = Number.parseInt(quantity, 10)
    if (!quantity.trim() || Number.isNaN(qty) || qty < 1) {
      toast.error('Please enter a valid quantity (at least 1).')
      return
    }

    if (!isFirebaseConfigured()) {
      toast.error('Request form is unavailable. Please try again later.')
      return
    }

    setSubmitting(true)
    try {
      await submitProductRequest({
        fullName,
        email,
        phone,
        productName,
        quantity: qty,
      })
      toast.success('Request submitted! We will get back to you soon.')
      onClose()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to submit request.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Request a Product" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-zinc-400">
          Tell us which attar you are looking for and we will do our best to
          help you find it.
        </p>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">
            Full name *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              className="input-luxury pl-10"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              autoComplete="name"
              required
              maxLength={120}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-zinc-400">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <input
                type="email"
                className="input-luxury pl-10"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                maxLength={200}
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-zinc-400">
              Phone *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <input
                type="tel"
                className="input-luxury pl-10"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="10-digit mobile"
                autoComplete="tel"
                required
                maxLength={30}
              />
            </div>
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">
            Product name *
          </label>
          <div className="relative">
            <Package className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              className="input-luxury pl-10"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="e.g. Royal Oud Attar"
              required
              maxLength={300}
            />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">
            Product quantity *
          </label>
          <input
            type="number"
            min={1}
            max={999}
            className="input-luxury w-32"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="e.g. 2"
            required
          />
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <Button type="submit" loading={submitting}>
            Submit request
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  )
}
